import { useMemo, useState } from "react";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import FormHelperText from "@mui/material/FormHelperText";
import { useTheme } from "@mui/material/styles";

import { InputLabelHeight } from "@covertree/types";
import { getInputLabelHeight } from "@covertree/utils";

import { Control, Controller, type FieldValues } from "react-hook-form";

interface DateRangeProps<TValues extends FieldValues> {
  id: string;
  startDateName: string;
  endDateName: string;
  label: React.ReactNode;
  control: Control<TValues>;
  disabledStartRange?: boolean;
  disabledEndRange?: boolean;
  labelHeight?: InputLabelHeight;
  minStartDate?: Date;
  maxStartDate?: Date;
  minEndDate?: Date;
  maxEndDate?: Date;
}

export function DateRange<TValues extends FieldValues>({
  id,
  startDateName,
  endDateName,
  label,
  control,
  disabledStartRange = false,
  disabledEndRange = false,
  labelHeight = InputLabelHeight.DEFAULT,
  minStartDate,
  maxStartDate,
  minEndDate,
  maxEndDate,
}: DateRangeProps<TValues>) {
  const theme = useTheme();
  const [endDateFocused, setEndDateFocused] = useState(false);

  const labelComponentHeight = useMemo(
    () => getInputLabelHeight(theme, labelHeight),
    [labelHeight, theme]
  );

  return (
    <FormControl variant="standard" fullWidth>
      <InputLabel
        htmlFor={id}
        id={`${id}-label`}
        sx={{
          position: "static",
          alignItems: "flex-end",
          display: "flex",
          transform: "none",
          height: labelComponentHeight,
          "& > p": {
            display: "flex",
            alignItems: "center",
            flex: 1,
            width: "100%",
            whiteSpace: "break-spaces",
          },
        }}>
        <Typography variant="body2" sx={{ margin: 0 }}>
          {label}
        </Typography>
      </InputLabel>
      <Grid
        container
        justifyContent="flex-start"
        gap={1}
        alignItems="flex-start">
        <Grid item>
          <Controller
            /* eslint-disable  @typescript-eslint/no-explicit-any */
            name={startDateName as any}
            control={control}
            defaultValue={undefined}
            render={({ field, fieldState: { error } }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  disabled={disabledStartRange}
                  views={["year", "month", "day"]}
                  sx={{
                    "& button": {
                      marginRight: 0,
                    },
                  }}
                  slotProps={{
                    textField: {
                      error: !!error,
                    },
                  }}
                  {...field}
                  {...(!!minStartDate
                    ? {
                        minDate: minStartDate,
                      }
                    : {})}
                  {...(!!maxStartDate
                    ? {
                        maxDate: maxStartDate,
                      }
                    : {})}
                  onAccept={() => setEndDateFocused(true)}
                />
                <FormHelperText
                  error
                  {...(!error?.message && { sx: { margin: 0 } })}>
                  {error?.message}
                </FormHelperText>
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Typography lineHeight={theme.spacing(8)}>{" - "}</Typography>
        <Grid item>
          <Controller
            /* eslint-disable  @typescript-eslint/no-explicit-any */
            name={endDateName as any}
            control={control}
            defaultValue={undefined}
            render={({ field, fieldState: { error } }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  disabled={disabledEndRange}
                  views={["year", "month", "day"]}
                  sx={{
                    "& button": {
                      marginRight: 0,
                    },
                  }}
                  slotProps={{
                    textField: {
                      error: !!error,
                      focused: endDateFocused,
                    },
                  }}
                  open={endDateFocused}
                  onOpen={() => setEndDateFocused(true)}
                  onClose={() => setEndDateFocused(false)}
                  {...field}
                  {...(!!minEndDate
                    ? {
                        minDate: minEndDate,
                      }
                    : {})}
                  {...(!!maxEndDate
                    ? {
                        maxDate: maxEndDate,
                      }
                    : {})}
                />
                <FormHelperText
                  error
                  {...(!error?.message && { sx: { margin: 0 } })}>
                  {error?.message}
                </FormHelperText>
              </LocalizationProvider>
            )}
          />
        </Grid>
      </Grid>
    </FormControl>
  );
}
